import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faCheckCircle,
    faClock,
    faExclamationTriangle,
    faFileShield,
    faInbox,
    faUserPlus,
} from '@fortawesome/free-solid-svg-icons';
import Layout from '../Layout';
import Footer from '../ui/Footer';
import imtLogoWhite from '../../assets/images/IMT_logo.png';
import { kycService } from '../../services/kycService';
import KYCInviteModal from './KYCInviteModal';
import './KYC.css';

const STATUS_BADGES = {
    invited: 'Invited',
    in_progress: 'In Progress',
    submitted: 'Submitted',
    approved: 'Approved',
    rejected: 'Rejected',
};

/**
 * Internal KYC Onboarding dashboard — cases organised per year, then per
 * partner (ClickUp requirement #8). Entry point: CRM dashboard actions menu.
 */
const KYCDashboard = () => {
    const history = useHistory();
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [inviteOpen, setInviteOpen] = useState(false);

    const loadCases = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await kycService.getCases();
            setCases(Array.isArray(data) ? data : data.results || []);
        } catch (err) {
            setError('Could not load KYC cases.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadCases();
    }, [loadCases]);

    const byYear = useMemo(() => {
        const groups = {};
        cases.forEach((kycCase) => {
            groups[kycCase.year] = groups[kycCase.year] || [];
            groups[kycCase.year].push(kycCase);
        });
        return Object.entries(groups)
            .sort((a, b) => Number(b[0]) - Number(a[0]))
            .map(([year, yearCases]) => ({
                year,
                cases: yearCases.sort((a, b) => a.company_name.localeCompare(b.company_name)),
            }));
    }, [cases]);

    const stats = useMemo(() => ({
        total: cases.length,
        inProgress: cases.filter((c) => c.status === 'invited' || c.status === 'in_progress').length,
        submitted: cases.filter((c) => c.status === 'submitted').length,
        approved: cases.filter((c) => c.status === 'approved').length,
        reviewDue: cases.filter((c) => c.review_due).length,
    }), [cases]);

    return (
        <Layout showLeftNavSidebar={true} showRightSidebar={false}>
            <div className="kyc-dashboard">
                <div className="kyc-header">
                    <div className="kyc-title-section">
                        <img src={imtLogoWhite} alt="IMT Bergbau AG" className="kyc-header-logo-img" />
                        <div>
                            <h1 className="hms-screen-title">KYC Onboarding</h1>
                            <p className="kyc-subtitle">
                                Partner KYC cases — organised per year, per partner
                            </p>
                        </div>
                    </div>
                    <div className="kyc-header-right">
                        <button
                            type="button"
                            className="kyc-back-link"
                            onClick={() => history.push('/dashboard/crm')}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} /> Back to CRM
                        </button>
                        <button
                            type="button"
                            className="kyc-btn-primary"
                            onClick={() => setInviteOpen(true)}
                        >
                            <FontAwesomeIcon icon={faUserPlus} /> Invite Partner
                        </button>
                    </div>
                </div>

                <div className="kyc-stats-row">
                    <div className="kyc-stat-card kyc-stat-card-total">
                        <div className="kyc-stat-icon"><FontAwesomeIcon icon={faFileShield} /></div>
                        <div>
                            <div className="kyc-stat-value">{loading ? '…' : stats.total}</div>
                            <div className="kyc-stat-label">Total Cases</div>
                        </div>
                    </div>
                    <div className="kyc-stat-card kyc-stat-card-progress">
                        <div className="kyc-stat-icon"><FontAwesomeIcon icon={faClock} /></div>
                        <div>
                            <div className="kyc-stat-value">{loading ? '…' : stats.inProgress}</div>
                            <div className="kyc-stat-label">With Partner</div>
                        </div>
                    </div>
                    <div className="kyc-stat-card kyc-stat-card-submitted">
                        <div className="kyc-stat-icon"><FontAwesomeIcon icon={faInbox} /></div>
                        <div>
                            <div className="kyc-stat-value">{loading ? '…' : stats.submitted}</div>
                            <div className="kyc-stat-label">Awaiting Review</div>
                        </div>
                    </div>
                    <div className="kyc-stat-card kyc-stat-card-approved">
                        <div className="kyc-stat-icon"><FontAwesomeIcon icon={faCheckCircle} /></div>
                        <div>
                            <div className="kyc-stat-value">{loading ? '…' : stats.approved}</div>
                            <div className="kyc-stat-label">Approved</div>
                        </div>
                    </div>
                    <div className="kyc-stat-card kyc-stat-card-due">
                        <div className="kyc-stat-icon"><FontAwesomeIcon icon={faExclamationTriangle} /></div>
                        <div>
                            <div className="kyc-stat-value">{loading ? '…' : stats.reviewDue}</div>
                            <div className="kyc-stat-label">Review Due</div>
                        </div>
                    </div>
                </div>

                <div className="kyc-content">
                    {loading ? (
                        <div className="kyc-loading">Loading KYC cases…</div>
                    ) : error ? (
                        <div className="kyc-card">
                            <div className="kyc-form-error">{error}</div>
                        </div>
                    ) : byYear.length === 0 ? (
                        <div className="kyc-card">
                            <div className="kyc-empty">
                                <div className="kyc-empty-title">No KYC cases yet</div>
                                <p>
                                    Invite your first partner — they will receive a secure link to
                                    upload their KYC documents and complete the onboarding form.
                                </p>
                                <button
                                    type="button"
                                    className="kyc-btn-primary"
                                    onClick={() => setInviteOpen(true)}
                                >
                                    <FontAwesomeIcon icon={faUserPlus} /> Invite Partner
                                </button>
                            </div>
                        </div>
                    ) : (
                        byYear.map(({ year, cases: yearCases }) => (
                            <div key={year}>
                                <h2 className="kyc-year-heading">
                                    {year}
                                    <span className="kyc-year-count">
                                        {yearCases.length} partner{yearCases.length === 1 ? '' : 's'}
                                    </span>
                                </h2>
                                <div className="kyc-card">
                                    <table className="kyc-case-table">
                                        <thead>
                                            <tr>
                                                <th>Partner</th>
                                                <th>Form</th>
                                                <th>Status</th>
                                                <th>Documents</th>
                                                <th>Contact</th>
                                                <th>Last Update</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {yearCases.map((kycCase) => (
                                                <tr
                                                    key={kycCase.id}
                                                    onClick={() => history.push(`/dashboard/crm/kyc/case/${kycCase.id}`)}
                                                >
                                                    <td className="kyc-company-cell">{kycCase.company_name}</td>
                                                    <td>
                                                        {kycCase.form_type === 'SA' ? (
                                                            <span className="kyc-badge kyc-badge-sa">KYC SA</span>
                                                        ) : (
                                                            'Standard'
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className={`kyc-badge kyc-badge-${kycCase.status}`}>
                                                            {STATUS_BADGES[kycCase.status] || kycCase.status}
                                                        </span>
                                                        {kycCase.review_due && (
                                                            <span className="kyc-badge kyc-badge-due" style={{ marginLeft: '0.4rem' }}>
                                                                Review due
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className="kyc-doc-progress">
                                                            {kycCase.required_documents_uploaded}/{kycCase.required_documents_total} required
                                                        </span>
                                                    </td>
                                                    <td>{kycCase.contact_email || '—'}</td>
                                                    <td>
                                                        {kycCase.updated_at
                                                            ? new Date(kycCase.updated_at).toLocaleDateString()
                                                            : '—'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {inviteOpen && (
                    <KYCInviteModal
                        onClose={() => setInviteOpen(false)}
                        onCreated={() => loadCases()}
                    />
                )}

                <Footer />
            </div>
        </Layout>
    );
};

export default KYCDashboard;
